/**
 * WeblabTrackerContent — read-only grid of weblab experiments with two
 * sub-views (matching the standalone tracker's two submenus):
 *
 *   - User Input : flat weblab grid (sortable, filterable, clickable)
 *   - Review     : summary tiles + TREE-GROUPED grid matching the tracker's
 *                  AG Grid Enterprise row-grouping:
 *                  ALL(n) → Weblab ID(n) → Treatment(n) → leaf rows
 *
 * The tree is built purely with MUI Table + indentation + expand/collapse
 * state, since FINN has no AG Grid dependency.
 *
 * Clicking a leaf row opens the detail panel (lazy-loaded full row).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MenuIcon from "@mui/icons-material/Menu";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import type { PluginReportContentProps } from "../../types";
import {
  WEBLAB_COLUMNS,
  formatWeblabValue,
  type WeblabColumn,
  type WeblabRow,
} from "./weblabColumns";
import type {
  WeblabTrackerData,
  DateRangeFilter,
} from "./useWeblabTrackerData";
import WeblabDetailPanel from "./WeblabDetailPanel";
import WeblabGroupPanel from "./WeblabGroupPanel";
import {
  fetchWeblabDetailById,
  fetchWeblabTeams,
} from "./weblabTrackerService";
import WeblabReviewSummary from "./WeblabReviewSummary";

// Filter operator options per column type (matching AG Grid Multi Filter)
const STRING_OPERATORS = [
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "starts_with", label: "Begins with" },
  { value: "ends_with", label: "Ends with" },
  { value: "blank", label: "Blank" },
  { value: "not_blank", label: "Not blank" },
];

const NUMBER_OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "gt", label: "Greater than" },
  { value: "gte", label: "Greater than or equal to" },
  { value: "lt", label: "Less than" },
  { value: "lte", label: "Less than or equal to" },
  { value: "between", label: "Between" },
  { value: "blank", label: "Blank" },
  { value: "not_blank", label: "Not blank" },
];

/** Determine if a column is numeric based on its format. */
function isNumericColumn(format: string | undefined): boolean {
  return format === "raw" || format === "number" || format === "currency";
}

// ─── Tree grouping types for Review tab ────────────────────────────────────

interface TreeLeaf {
  type: "leaf";
  row: WeblabRow;
}

interface TreeGroup {
  type: "group";
  key: string;
  label: string;
  count: number;
  level: number; // 0 = ALL, 1 = Weblab ID, 2 = Treatment
  children: (TreeGroup | TreeLeaf)[];
}

/**
 * Build the 3-level tree from flat rows:
 *   Level 0: ALL (single root)
 *   Level 1: weblab_id
 *   Level 2: baseline_treatment:treatment (e.g. "C:T2")
 *   Leaves: individual rows (per marketplace)
 */
function buildTree(rows: WeblabRow[]): TreeGroup {
  const byWeblab = new Map<string, WeblabRow[]>();
  for (const row of rows) {
    const wid = (row.weblab_id as string) ?? "Unknown";
    if (!byWeblab.has(wid)) byWeblab.set(wid, []);
    byWeblab.get(wid)!.push(row);
  }

  const weblabGroups: TreeGroup[] = [];
  for (const [wid, wRows] of byWeblab.entries()) {
    const byTreatment = new Map<string, WeblabRow[]>();
    for (const r of wRows) {
      const base = (r.baseline_treatment as string) ?? "C";
      const treat = (r.treatment as string) ?? "?";
      const tKey = `${base}:${treat}`;
      if (!byTreatment.has(tKey)) byTreatment.set(tKey, []);
      byTreatment.get(tKey)!.push(r);
    }

    const treatmentGroups: TreeGroup[] = [];
    for (const [tKey, tRows] of byTreatment.entries()) {
      treatmentGroups.push({
        type: "group",
        key: `${wid}/${tKey}`,
        label: tKey,
        count: tRows.length,
        level: 2,
        children: tRows.map((r) => ({ type: "leaf" as const, row: r })),
      });
    }

    weblabGroups.push({
      type: "group",
      key: wid,
      label: wid,
      count: wRows.length,
      level: 1,
      children: treatmentGroups,
    });
  }

  return {
    type: "group",
    key: "ALL",
    label: "ALL",
    count: rows.length,
    level: 0,
    children: weblabGroups,
  };
}

// ─── Component ─────────────────────────────────────────────────────────────

const WeblabTrackerContent: React.FC<PluginReportContentProps> = ({
  data,
  isLoading,
  error,
  refetch,
  effectiveRole,
  currentTeamId,
  onFiltersChange,
}) => {
  const typed = data as WeblabTrackerData | null;

  const [quickFilter, setQuickFilter] = useState("");
  const [sortField, setSortField] = useState<string>("id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Export permission: only admin/operator when on Weblab Tracker team (id 104)
  const WEBLAB_TRACKER_TEAM_ID = 104;
  const canExport =
    currentTeamId === WEBLAB_TRACKER_TEAM_ID &&
    (effectiveRole === "admin" || effectiveRole === "operator");

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<{
    el: HTMLElement;
    field: string;
  } | null>(null);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleResizeStart = (
    field: string,
    startX: number,
    startWidth: number
  ) => {
    const onMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [field]: newWidth }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  const [selectedRow, setSelectedRow] = useState<WeblabRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [selectedWeblabGroup, setSelectedWeblabGroup] = useState<{
    weblabId: string;
    rows: WeblabRow[];
    treatment?: string; // e.g. "C:T1" — set when a treatment group is clicked
  } | null>(null);

  // Date range filter: "currentYear" (default) or "all".
  const [dateRangeFilter, setDateRangeFilter] =
    useState<DateRangeFilter>("currentYear");

  // Team selector state (embedded in toolbar for compact layout)
  const [teams, setTeams] = useState<string[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const userPickedTeamRef = useRef(false);

  useEffect(() => {
    // Default to "All Teams" (empty-string sentinel = no team filter).
    // Fire the filter immediately so the all-teams data fetch starts in
    // parallel with loading the team list, instead of waiting on it.
    if (!userPickedTeamRef.current) {
      userPickedTeamRef.current = true;
      // Derive the initial dateRange from the SAME state the control renders
      // from (dateRangeFilter), NOT a hardcoded literal. A literal decoupled
      // the first server-side fetch from the control's displayed value, so on
      // first load the query ran with a different date filter than the UI
      // showed as selected. Reading dateRangeFilter keeps one source of truth.
      onFiltersChange?.({
        teamName: "",
        dateRange: dateRangeFilter,
      });
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchWeblabTeams();
        if (!cancelled) setTeams(list);
      } catch (err) {
        console.warn("Failed to load Weblab teams:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onFiltersChange, dateRangeFilter]);

  // The server query applies the team filter. Keep the same filter client-side
  // so stale rows cannot briefly appear while a new response is loading.
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );
  const [columnFilterOps, setColumnFilterOps] = useState<
    Record<string, string>
  >({});
  const [columnSetFilters, setColumnSetFilters] = useState<
    Record<string, Set<string>>
  >({});
  const [filterSearch, setFilterSearch] = useState("");
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(
    new Set()
  );
  const [filterValueSearch, setFilterValueSearch] = useState<
    Record<string, string>
  >({});

  // Column chooser
  const [visibleFields, setVisibleFields] = useState<Set<string>>(
    () =>
      new Set(
        WEBLAB_COLUMNS.filter((c) => c.defaultVisible).map(
          (c) => c.field as string
        )
      )
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    WEBLAB_COLUMNS.map((c) => c.field as string)
  );
  const [rightPanel, setRightPanel] = useState<"filters" | "columns" | null>(
    null
  );
  const [panelWidth, setPanelWidth] = useState(280);

  // Tree expand/collapse state (for Review tab)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const visibleColumns = useMemo(
    () =>
      columnOrder
        .map((field) =>
          WEBLAB_COLUMNS.find((c) => (c.field as string) === field)
        )
        .filter(
          (c): c is WeblabColumn =>
            c != null && visibleFields.has(c.field as string)
        ),
    [visibleFields, columnOrder]
  );

  // Pre-compute unique values per column (expensive: iterates all rows).
  // Memoized so it only recalculates when rows change, not on every keystroke.
  // Base rows: after team + current year filter, before column filters.
  // Used for unique values in filter checkboxes (avoids circular dep).
  const baseRows = useMemo(() => {
    // Date range is filtered SERVER-SIDE (SQL WHERE clause); rows arriving
    // here are already scoped to the selected date range. Only apply the
    // client-side team filter for the unique-values computation.
    let all = typed?.rows ?? [];
    if (selectedTeam) {
      all = all.filter((r) => r.team_name === selectedTeam);
    }
    return all;
  }, [typed, selectedTeam]);

  const uniqueValuesMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const col of WEBLAB_COLUMNS) {
      const field = col.field as string;
      const vals = new Set<string>();
      for (const row of baseRows) {
        const v = String(row[field] ?? "");
        if (v !== "") vals.add(v);
      }
      map[field] = Array.from(vals).sort();
    }
    return map;
  }, [baseRows]);

  const toggleColumn = (field: string) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  /** Clear all filters (quick-filter + per-column). */

  /**
   * Row click → lazy-load the FULL row by id.
   */
  const handleRowClick = useCallback(
    async (row: WeblabRow) => {
      const isSame =
        selectedRow != null &&
        selectedRow.id === row.id &&
        selectedRow.weblab_id === row.weblab_id;
      if (isSame) {
        setSelectedRow(null);
        setDetailError(null);
        return;
      }
      setSelectedRow(row);
      setSelectedWeblabGroup(null);
      setDetailError(null);
      if (row.id == null || row.id === "") return;
      setDetailLoading(true);
      try {
        const full = await fetchWeblabDetailById(row.id as string);
        if (full) {
          setSelectedRow((prev) =>
            prev && prev.id === row.id
              ? ({ ...prev, ...full } as WeblabRow)
              : prev
          );
        }
      } catch (err: any) {
        setDetailError(err?.message ?? "Failed to load weblab detail");
      } finally {
        setDetailLoading(false);
      }
    },
    [selectedRow]
  );

  // ─── Filter + sort rows ─────────────────────────────────────────────────

  const rows = useMemo(() => {
    let all = typed?.rows ?? [];
    console.log("[WeblabTracker DEBUG] total rows from Athena:", all.length);
    if (all.length > 0) {
      const uniqueTeams = [...new Set(all.map((r: any) => r.team_name))].sort();
      console.log(
        "[WeblabTracker DEBUG] unique team_name values in data:",
        uniqueTeams.slice(0, 20)
      );
    }

    // Defensive client-side team filter; the Athena query is also team-scoped.
    if (selectedTeam) {
      all = all.filter((r) => r.team_name === selectedTeam);
    }
    let filtered = all;

    console.log(
      "[WeblabTracker DEBUG] after team filter:",
      filtered.length,
      "team:",
      selectedTeam || "All"
    );

    // NOTE: date-range filtering is applied SERVER-SIDE in the SQL WHERE
    // clause (see useWeblabTrackerData.buildWhereClause). We deliberately do
    // NOT re-filter by date here — a second client-side pass was a redundant
    // source of truth that could drift out of sync with the query on first
    // load. Server-side SQL is the single source of truth for date range.

    // Quick filter
    const q = quickFilter.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((r) =>
        [r.weblab_id, r.owners, r.short_description, r.team_name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    // Per-column filters (operator-aware)
    const activeColFilters = Object.entries(columnFilters).filter(([field]) => {
      const op = columnFilterOps[field] ?? "contains";
      if (op === "blank" || op === "not_blank") return true;
      return (columnFilters[field] ?? "").trim() !== "";
    });
    if (activeColFilters.length > 0) {
      filtered = filtered.filter((r) =>
        activeColFilters.every(([field, val]) => {
          const op = columnFilterOps[field] ?? "contains";
          const raw = r[field];
          const cellStr = String(raw ?? "").toLowerCase();
          const filterVal = val.trim().toLowerCase();
          switch (op) {
            case "contains":
              return cellStr.includes(filterVal);
            case "not_contains":
              return !cellStr.includes(filterVal);
            case "equals":
              return cellStr === filterVal;
            case "not_equals":
              return cellStr !== filterVal;
            case "starts_with":
              return cellStr.startsWith(filterVal);
            case "ends_with":
              return cellStr.endsWith(filterVal);
            case "gt": {
              const n = Number(raw),
                f = Number(val);
              return !isNaN(n) && !isNaN(f) && n > f;
            }
            case "gte": {
              const n = Number(raw),
                f = Number(val);
              return !isNaN(n) && !isNaN(f) && n >= f;
            }
            case "lt": {
              const n = Number(raw),
                f = Number(val);
              return !isNaN(n) && !isNaN(f) && n < f;
            }
            case "lte": {
              const n = Number(raw),
                f = Number(val);
              return !isNaN(n) && !isNaN(f) && n <= f;
            }
            case "between": {
              const parts = val.split(",");
              if (parts.length < 2) return true;
              const n = Number(raw),
                lo = Number(parts[0]),
                hi = Number(parts[1]);
              return (
                !isNaN(n) && !isNaN(lo) && !isNaN(hi) && n >= lo && n <= hi
              );
            }
            case "blank":
              return raw == null || String(raw).trim() === "";
            case "not_blank":
              return raw != null && String(raw).trim() !== "";
            default:
              return cellStr.includes(filterVal);
          }
        })
      );
    }

    // Set filters (checkbox exclusions)
    const activeSetFilters = Object.entries(columnSetFilters).filter(
      ([, excluded]) => excluded.size > 0
    );
    if (activeSetFilters.length > 0) {
      filtered = filtered.filter((r) =>
        activeSetFilters.every(
          ([field, excluded]) => !excluded.has(String(r[field] ?? ""))
        )
      );
    }

    // Sort
    const sorted = [...filtered].sort((a: WeblabRow, b: WeblabRow) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      const an = Number(av);
      const bn = Number(bv);
      let cmp: number;
      if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== "" && bv !== "") {
        cmp = an - bn;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [
    typed,
    quickFilter,
    columnFilters,
    columnFilterOps,
    columnSetFilters,
    sortField,
    sortDirection,
    selectedTeam,
  ]);

  // ─── Tree for Review tab ────────────────────────────────────────────────

  const tree = useMemo(() => buildTree(rows), [rows]);

  // Reset tree expansion to first two levels whenever the tree's root-level
  // groups change (team switch, filter change, etc.). Using `tree` as the
  // dependency ensures it fires when the grouping structure changes, not just
  // when the total row count coincidentally stays the same.
  useEffect(() => {
    const defaultExpanded = new Set<string>();
    defaultExpanded.add(tree.key);
    for (const child of tree.children) {
      if (child.type === "group") {
        defaultExpanded.add(child.key);
      }
    }
    setExpanded(defaultExpanded);
  }, [tree]);

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Memoize rendered tree rows so filter-panel interactions don't re-render the grid.
  // renderTreeRows is intentionally omitted from the deps: it's recreated every
  // render, so listing it would defeat the memoization. The reactive values it
  // closes over (expanded, selectedRow) are tracked explicitly instead.
  const renderedTree = useMemo(
    () => renderTreeRows(tree, visibleColumns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tree, visibleColumns, expanded, selectedRow]
  );

  // ─── Render helpers ─────────────────────────────────────────────────────

  // ─── Tree rendering for Review ──────────────────────────────────────────

  /**
   * Recursively render tree nodes. Group rows span the group columns area.
   * Leaf rows render in the data columns with empty group column cells.
   */
  function renderTreeRows(
    node: TreeGroup,
    cols: WeblabColumn[]
  ): React.ReactNode[] {
    const dataCols = cols.filter(
      (c) =>
        c.field !== "weblab_id" &&
        c.field !== "baseline_treatment" &&
        c.field !== "treatment"
    );
    const results: React.ReactNode[] = [];

    // Level 0: ALL root
    const isRootExpanded = expanded.has(node.key);
    results.push(
      <TableRow
        key={node.key}
        hover
        onClick={() => {
          setSelectedWeblabGroup(null);
          setSelectedRow(null);
        }}
        sx={{ cursor: "pointer", bgcolor: "grey.100" }}
      >
        <TableCell sx={{ py: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="span"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                toggleExpand(node.key);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                borderRadius: "4px",
                p: "2px",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {isRootExpanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </Box>
            <Typography variant="body2" fontWeight={700} sx={{ ml: 0.5 }}>
              {node.label} ({node.count})
            </Typography>
          </Box>
        </TableCell>
        <TableCell />
        <TableCell />
        {dataCols.map((c) => (
          <TableCell key={c.field as string} />
        ))}
      </TableRow>
    );

    if (!isRootExpanded) return results;

    // Level 1: Weblab ID groups
    for (const l1 of node.children) {
      if (l1.type !== "group") continue;
      const isL1Expanded = expanded.has(l1.key);
      results.push(
        <TableRow
          key={l1.key}
          hover
          onClick={() => {
            // Collect all leaf rows under this weblab group
            const leaves: WeblabRow[] = [];
            for (const l2 of l1.children) {
              if (l2.type === "leaf") leaves.push(l2.row);
              else if (l2.type === "group") {
                for (const l3 of l2.children)
                  if (l3.type === "leaf") leaves.push(l3.row);
              }
            }
            setSelectedWeblabGroup({ weblabId: l1.label, rows: leaves });
            setSelectedRow(null);
          }}
          sx={{ cursor: "pointer", bgcolor: "grey.50" }}
        >
          <TableCell sx={{ py: 0.5, pl: 4 }} />
          <TableCell sx={{ py: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="span"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  toggleExpand(l1.key);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: "4px",
                  p: "2px",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {isL1Expanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </Box>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ ml: 0.5 }}
                noWrap
              >
                {l1.label.length > 30
                  ? `${l1.label.substring(0, 27)}…`
                  : l1.label}{" "}
                ({l1.count})
              </Typography>
            </Box>
          </TableCell>
          <TableCell />
          {dataCols.map((c) => (
            <TableCell key={c.field as string} />
          ))}
        </TableRow>
      );

      if (!isL1Expanded) continue;

      // Level 2: Treatment groups
      for (const l2 of l1.children) {
        if (l2.type !== "group") continue;
        const isL2Expanded = expanded.has(l2.key);
        results.push(
          <TableRow
            key={l2.key}
            hover
            onClick={() => {
              // Collect leaf rows under this treatment group
              const leaves: WeblabRow[] = [];
              for (const l3 of l2.children)
                if (l3.type === "leaf") leaves.push(l3.row);
              setSelectedWeblabGroup({
                weblabId: l1.label,
                rows: leaves,
                treatment: l2.label,
              });
              setSelectedRow(null);
            }}
            sx={{ cursor: "pointer" }}
          >
            <TableCell sx={{ py: 0.5 }} />
            <TableCell sx={{ py: 0.5 }} />
            <TableCell sx={{ py: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                <Box
                  component="span"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    toggleExpand(l2.key);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "4px",
                    p: "2px",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {isL2Expanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </Box>
                <Typography variant="body2" fontWeight={500} sx={{ ml: 0.5 }}>
                  {l2.label} ({l2.count})
                </Typography>
              </Box>
            </TableCell>
            {dataCols.map((c) => (
              <TableCell key={c.field as string} />
            ))}
          </TableRow>
        );

        if (!isL2Expanded) continue;

        // Leaf rows
        for (const leaf of l2.children) {
          if (leaf.type !== "leaf") continue;
          const row = leaf.row;
          const isSelected =
            selectedRow != null &&
            selectedRow.id === row.id &&
            selectedRow.weblab_id === row.weblab_id;
          results.push(
            <TableRow
              key={`leaf-${row.id ?? row.weblab_id}-${row.marketplace_id}`}
              hover
              selected={isSelected}
              onClick={() => handleRowClick(row)}
              sx={{ cursor: "pointer" }}
            >
              {/* Empty group columns for leaf alignment */}
              <TableCell />
              <TableCell />
              <TableCell />
              {dataCols.map((col) => {
                const raw = row[col.field as string];
                return (
                  <TableCell
                    key={col.field as string}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {formatWeblabValue(raw, col.format)}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        }
      }
    }

    return results;
  }

  const handleExportCsv = useCallback(() => {
    if (!rows.length) return;
    const cols = WEBLAB_COLUMNS;
    const header = cols.map((c) => `"${c.header}"`).join(",");
    const csvLines = rows.map((row) =>
      cols
        .map((c) => {
          const v = formatWeblabValue(row[c.field as string], c.format);
          return `"${String(v).replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [header, ...csvLines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "weblab-tracker-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  // While loading (first load OR team switch), show ONLY a blank area with
  // a centered loading message — no tiles, toolbar, or grid.
  //
  // On the very first load (`typed` is null) the DashboardShell already
  // shows its own "Loading dashboard data…" banner, so return null to avoid
  // two messages. On refetches (team switch) the shell banner is suppressed
  // and this message is the single loading indicator.
  if (isLoading) {
    if (!typed) return null;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          py: 14,
        }}
      >
        <CircularProgress size={36} thickness={4} />
        <Typography variant="body1" color="text.secondary">
          {selectedTeam
            ? `Loading ${selectedTeam} weblabs…`
            : "Loading weblabs…"}
        </Typography>
      </Box>
    );
  }

  // ─── Toolbar (shared logic, different controls per sub-view) ─────────────

  const renderToolbar = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        py: 0.5,
      }}
    >
      {/* Team selector */}
      <FormControl size="small" sx={{ flex: 1 }}>
        <InputLabel id="wt-team-label" shrink>
          Team
        </InputLabel>
        <Select
          labelId="wt-team-label"
          label="Team"
          value={selectedTeam}
          onChange={(e) => {
            const team = e.target.value as string;
            setSelectedTeam(team);
            userPickedTeamRef.current = true;
            // Reset selections so the ALL summary shows on team switch
            setSelectedWeblabGroup(null);
            setSelectedRow(null);
            // Trigger server-side re-fetch with team filter in SQL.
            // Empty string = "All Teams" (no team filter); null would make
            // the data hook treat the filter as "not ready" and skip fetching.
            onFiltersChange?.({
              teamName: team,
              dateRange: dateRangeFilter,
            });
          }}
          displayEmpty
          notched
        >
          <MenuItem value="">All</MenuItem>
          {teams.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Date range toggle: checked = Current Year (launched this year),
          unchecked = All. A single checkbox replaces the old dropdown. */}
      <FormControlLabel
        sx={{ ml: 0, whiteSpace: "nowrap" }}
        control={
          <Checkbox
            size="small"
            checked={dateRangeFilter === "currentYear"}
            onChange={(e) => {
              const newRange: DateRangeFilter = e.target.checked
                ? "currentYear"
                : "all";
              setDateRangeFilter(newRange);
              // Trigger server-side re-fetch with new date range in SQL
              onFiltersChange?.({
                teamName: selectedTeam,
                dateRange: newRange,
              });
            }}
          />
        }
        label="Current Year"
      />

      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <Tooltip title="Refresh Data">
          <IconButton size="small" onClick={() => refetch?.()}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <Tooltip title="Clear Column Filters">
          <IconButton
            size="small"
            onClick={() => {
              setColumnFilters({});
              setColumnFilterOps({});
              setColumnSetFilters({});
            }}
          >
            <FilterAltOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        {canExport && (
          <Tooltip title="Export Data (CSV)">
            <IconButton size="small" onClick={handleExportCsv}>
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Search — takes more space */}
      <TextField
        size="small"
        placeholder="Search..."
        value={quickFilter}
        onChange={(e) => setQuickFilter(e.target.value)}
        sx={{ flex: 2 }}
      />
    </Box>
  );

  // ─── Main render ────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Review sub-view: summary tiles on top */}
      {!selectedWeblabGroup && !selectedRow && (
        <WeblabReviewSummary rows={rows} />
      )}

      {/* Detail panel */}
      {selectedWeblabGroup && !selectedRow && (
        <WeblabGroupPanel
          weblabId={selectedWeblabGroup.weblabId}
          rows={selectedWeblabGroup.rows}
          treatment={selectedWeblabGroup.treatment}
        />
      )}
      {selectedRow && (
        <WeblabDetailPanel
          row={selectedRow}
          loading={detailLoading}
          error={detailError}
        />
      )}

      {/* Toolbar */}
      {renderToolbar()}

      <Box sx={{ display: "flex", maxHeight: "70vh", overflow: "visible" }}>
        <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
          {/* stickyHeader keeps column headers visible while the body scrolls
              within the 70vh container */}
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {/* Three group column headers matching the tracker */}
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                  All(Group Column)
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>
                  Weblab ID(Group Column)
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>
                  Treatment(Group Column)
                </TableCell>
                {/* Remaining data columns */}
                {visibleColumns
                  .filter(
                    (c) =>
                      c.field !== "weblab_id" &&
                      c.field !== "baseline_treatment" &&
                      c.field !== "treatment"
                  )
                  .map((col) => (
                    <TableCell
                      key={col.field as string}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        userSelect: "none",
                        // NOTE: no `position` override here — stickyHeader
                        // applies position:sticky to header cells, and sticky
                        // elements still anchor the absolute resize handle.
                        width: columnWidths[col.field as string] || undefined,
                        minWidth: columnWidths[col.field as string] || 100,
                      }}
                      onClick={() => handleSort(col.field as string)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <span style={{ flex: 1 }}>{col.header}</span>
                        {sortField === col.field &&
                          (sortDirection === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5 }} />
                          ))}
                        <MenuIcon
                          sx={{
                            fontSize: 16,
                            opacity: 0.3,
                            ml: 0.5,
                            "&:hover": { opacity: 1 },
                          }}
                          onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                            e.stopPropagation();
                            setFilterMenuAnchor({
                              el: e.currentTarget.closest("th") as HTMLElement,
                              field: col.field as string,
                            });
                          }}
                        />
                      </Box>
                      {/* Resize handle */}
                      <Box
                        component="span"
                        onMouseDown={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          const cell = (e.target as HTMLElement).closest(
                            "th, td"
                          ) as HTMLElement;
                          handleResizeStart(
                            col.field as string,
                            e.clientX,
                            cell?.offsetWidth ?? 100
                          );
                        }}
                        sx={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          cursor: "col-resize",
                          "&:hover": {
                            bgcolor: "primary.main",
                            opacity: 0.3,
                          },
                        }}
                      />
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {renderedTree}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 3}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      No weblabs to show.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Resize handle between table and sidebar */}
        <Box
          sx={{
            width: 4,
            cursor: "col-resize",
            bgcolor: "divider",
            "&:hover": { bgcolor: "primary.main", opacity: 0.5 },
            transition: "background-color 150ms",
            flexShrink: 0,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = panelWidth;
            const onMove = (ev: MouseEvent) => {
              const diff = startX - ev.clientX;
              setPanelWidth(Math.max(180, Math.min(500, startWidth + diff)));
            };
            const onUp = () => {
              document.removeEventListener("mousemove", onMove);
              document.removeEventListener("mouseup", onUp);
            };
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
          }}
        />

        {/* Right sidebar — Filters & Columns (vertical tabs like AG Grid) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            borderLeft: 1,
            borderColor: "divider",
            alignSelf: "stretch",
          }}
        >
          {/* Vertical tab labels */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              bgcolor: "grey.100",
              borderLeft: 1,
              borderColor: "divider",
            }}
          >
            <Box
              component="button"
              onClick={() =>
                setRightPanel((p) => (p === "filters" ? null : "filters"))
              }
              sx={{
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                border: "none",
                background: rightPanel === "filters" ? "white" : "transparent",
                cursor: "pointer",
                px: 1,
                py: 2,
                fontSize: 12,
                fontWeight: rightPanel === "filters" ? 700 : 500,
                color:
                  rightPanel === "filters" ? "primary.main" : "text.secondary",
                borderRight: rightPanel === "filters" ? "2px solid" : "none",
                borderRightColor: "primary.main",
                "&:hover": { bgcolor: "grey.200" },
                outline: "none",
              }}
            >
              Filters
            </Box>
            <Divider />
            <Box
              component="button"
              onClick={() =>
                setRightPanel((p) => (p === "columns" ? null : "columns"))
              }
              sx={{
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                border: "none",
                background: rightPanel === "columns" ? "white" : "transparent",
                cursor: "pointer",
                px: 1,
                py: 2,
                fontSize: 12,
                fontWeight: rightPanel === "columns" ? 700 : 500,
                color:
                  rightPanel === "columns" ? "primary.main" : "text.secondary",
                borderRight: rightPanel === "columns" ? "2px solid" : "none",
                borderRightColor: "primary.main",
                "&:hover": { bgcolor: "grey.200" },
                outline: "none",
              }}
            >
              Columns
            </Box>
          </Box>

          {/* Panel content */}
          {rightPanel && (
            <Box
              sx={{
                width: panelWidth,
                minWidth: 180,
                maxWidth: 500,
                maxHeight: "70vh",
                overflow: "auto",
                p: 1.5,
                bgcolor: "background.paper",
                borderLeft: 1,
                borderColor: "divider",
              }}
            >
              {rightPanel === "filters" && (
                <Box>
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                  />
                  <List dense disablePadding>
                    {visibleColumns
                      .filter((col) =>
                        col.header
                          .toLowerCase()
                          .includes(filterSearch.toLowerCase())
                      )
                      .map((col) => {
                        const field = col.field as string;
                        const isExpFilterOpen = expandedFilters.has(field);
                        const uniqueValues = uniqueValuesMap[field] ?? [];
                        const excluded =
                          columnSetFilters[field] ?? new Set<string>();
                        const valSearch = (
                          filterValueSearch[field] ?? ""
                        ).toLowerCase();
                        const filteredValues = valSearch
                          ? uniqueValues.filter((v) =>
                              v.toLowerCase().includes(valSearch)
                            )
                          : uniqueValues;
                        const allChecked = excluded.size === 0;
                        return (
                          <Box key={field}>
                            <ListItemButton
                              dense
                              onClick={() =>
                                setExpandedFilters((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(field)) next.delete(field);
                                  else next.add(field);
                                  return next;
                                })
                              }
                              sx={{ px: 0.5 }}
                            >
                              {isExpFilterOpen ? (
                                <ExpandMoreIcon fontSize="small" />
                              ) : (
                                <ChevronRightIcon fontSize="small" />
                              )}
                              <ListItemText
                                primary={col.header}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontWeight: 600,
                                }}
                              />
                            </ListItemButton>
                            <Collapse in={isExpFilterOpen}>
                              <Box sx={{ pl: 3, pr: 0.5, pb: 1 }}>
                                <Select
                                  size="small"
                                  value={
                                    columnFilterOps[field] ??
                                    (isNumericColumn(col.format)
                                      ? "equals"
                                      : "contains")
                                  }
                                  onChange={(e) =>
                                    setColumnFilterOps((prev) => ({
                                      ...prev,
                                      [field]: e.target.value as string,
                                    }))
                                  }
                                  fullWidth
                                  variant="outlined"
                                  sx={{ mb: 0.5, mt: 0.5 }}
                                >
                                  {(isNumericColumn(col.format)
                                    ? NUMBER_OPERATORS
                                    : STRING_OPERATORS
                                  ).map((op) => (
                                    <MenuItem key={op.value} value={op.value}>
                                      {op.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {!(
                                  (columnFilterOps[field] ?? "") === "blank" ||
                                  (columnFilterOps[field] ?? "") === "not_blank"
                                ) && (
                                  <TextField
                                    size="small"
                                    placeholder={
                                      (columnFilterOps[field] ?? "") ===
                                      "between"
                                        ? "min,max"
                                        : "Filter..."
                                    }
                                    value={columnFilters[field] ?? ""}
                                    onChange={(e) =>
                                      setColumnFilters((prev) => ({
                                        ...prev,
                                        [field]: e.target.value,
                                      }))
                                    }
                                    fullWidth
                                    sx={{ mb: 1 }}
                                  />
                                )}
                                <Divider sx={{ my: 1 }} />
                                <TextField
                                  size="small"
                                  placeholder="Search..."
                                  value={filterValueSearch[field] ?? ""}
                                  onChange={(e) =>
                                    setFilterValueSearch((prev) => ({
                                      ...prev,
                                      [field]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                  sx={{ mb: 0.5 }}
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={allChecked}
                                      indeterminate={
                                        !allChecked &&
                                        excluded.size < uniqueValues.length
                                      }
                                      onChange={() => {
                                        setColumnSetFilters((prev) => {
                                          const next = { ...prev };
                                          if (allChecked) {
                                            next[field] = new Set(uniqueValues);
                                          } else {
                                            next[field] = new Set();
                                          }
                                          return next;
                                        });
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      (Select All)
                                    </Typography>
                                  }
                                  sx={{ ml: 0 }}
                                />
                                <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                                  {filteredValues.slice(0, 100).map((val) => (
                                    <FormControlLabel
                                      key={val}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={!excluded.has(val)}
                                          onChange={() => {
                                            setColumnSetFilters((prev) => {
                                              const next = { ...prev };
                                              const excl = new Set(
                                                next[field] ?? []
                                              );
                                              if (excl.has(val))
                                                excl.delete(val);
                                              else excl.add(val);
                                              next[field] = excl;
                                              return next;
                                            });
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography
                                          variant="body2"
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {val}
                                        </Typography>
                                      }
                                      sx={{ ml: 0, mr: 0, width: "100%" }}
                                    />
                                  ))}
                                  {filteredValues.length > 100 && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ pl: 3, pt: 0.5 }}
                                    >
                                      …and {filteredValues.length - 100} more
                                      (use Search to narrow)
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                  </List>
                </Box>
              )}
              {rightPanel === "columns" && (
                <Box>
                  {columnOrder.map((field, idx) => {
                    const col = WEBLAB_COLUMNS.find(
                      (c) => (c.field as string) === field
                    );
                    if (!col) return null;
                    return (
                      <Box
                        key={field}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", String(idx));
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromIdx = Number(
                            e.dataTransfer.getData("text/plain")
                          );
                          if (fromIdx === idx) return;
                          setColumnOrder((prev) => {
                            const next = [...prev];
                            const [moved] = next.splice(fromIdx, 1);
                            next.splice(idx, 0, moved);
                            return next;
                          });
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 0.25,
                          cursor: "grab",
                          "&:hover": { bgcolor: "grey.50" },
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={visibleFields.has(field)}
                          onChange={() => toggleColumn(field)}
                          sx={{ p: 0.5 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mx: 0.5,
                            cursor: "grab",
                            userSelect: "none",
                          }}
                        >
                          ⠿
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {col.header}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {/* Column filter popover (triggered by ≡ icon on header) */}
      <Popover
        open={Boolean(filterMenuAnchor)}
        anchorEl={filterMenuAnchor?.el}
        onClose={() => setFilterMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {filterMenuAnchor &&
          (() => {
            const field = filterMenuAnchor.field;
            const uniqueValues = uniqueValuesMap[field] ?? [];
            const excluded = columnSetFilters[field] ?? new Set<string>();
            const allChecked = excluded.size === 0;
            return (
              <Box
                sx={{ p: 1.5, width: 280, maxHeight: 400, overflow: "auto" }}
              >
                {/* Operator dropdown */}
                <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                  <Select
                    value={columnFilterOps[field] ?? "contains"}
                    onChange={(e) =>
                      setColumnFilterOps((prev) => ({
                        ...prev,
                        [field]: e.target.value,
                      }))
                    }
                    displayEmpty
                  >
                    <MenuItem value="contains">Contains</MenuItem>
                    <MenuItem value="not_contains">Does not contain</MenuItem>
                    <MenuItem value="equals">Equals</MenuItem>
                    <MenuItem value="not_equals">Does not equal</MenuItem>
                    <MenuItem value="starts_with">Begins with</MenuItem>
                    <MenuItem value="ends_with">Ends with</MenuItem>
                    <MenuItem value="blank">Blank</MenuItem>
                    <MenuItem value="not_blank">Not blank</MenuItem>
                  </Select>
                </FormControl>

                {/* Text filter */}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  value={columnFilters[field] ?? ""}
                  onChange={(e) =>
                    setColumnFilters((prev) => ({
                      ...prev,
                      [field]: e.target.value,
                    }))
                  }
                  sx={{ mb: 1 }}
                />

                <Divider sx={{ my: 1 }} />

                {/* Set filter checkboxes */}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search..."
                  value={filterValueSearch[field] ?? ""}
                  onChange={(e) =>
                    setFilterValueSearch((prev: any) => ({
                      ...prev,
                      [field]: e.target.value,
                    }))
                  }
                  sx={{ mb: 1 }}
                />

                {/* Select All */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    mb: 0.5,
                  }}
                  onClick={() => {
                    if (allChecked) {
                      setColumnSetFilters((prev: any) => ({
                        ...prev,
                        [field]: new Set(uniqueValues),
                      }));
                    } else {
                      setColumnSetFilters((prev: any) => {
                        const next = { ...prev };
                        delete next[field];
                        return next;
                      });
                    }
                  }}
                >
                  <Checkbox size="small" checked={allChecked} sx={{ p: 0.5 }} />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    (Select All)
                  </Typography>
                </Box>

                {/* Individual values */}
                {uniqueValues
                  .filter(
                    (v) =>
                      !(filterValueSearch[field] ?? "") ||
                      v
                        .toLowerCase()
                        .includes(
                          (filterValueSearch[field] ?? "").toLowerCase()
                        )
                  )
                  .slice(0, 100)
                  .map((val) => (
                    <Box
                      key={val}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setColumnSetFilters((prev: any) => {
                          const current = new Set(prev[field] ?? []);
                          if (current.has(val)) current.delete(val);
                          else current.add(val);
                          return { ...prev, [field]: current };
                        });
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={!excluded.has(val)}
                        sx={{ p: 0.5 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 0.5, wordBreak: "break-word" }}
                      >
                        {val}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            );
          })()}
      </Popover>
    </Box>
  );
};

export default WeblabTrackerContent;
